import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeftIcon,
  UserIcon,
  EnvelopeIcon,
  CalendarIcon,
  CreditCardIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  CurrencyDollarIcon,
} from '@heroicons/react/24/outline';
import useSubscriptionDetail from './useSubscriptionDetail';

export default function SubscriptionDetail() {
const location= useLocation();
const { subscriptionId } =location?.state||{}
  // console.log("params-----------",location)
  const navigate = useNavigate();
  const {
    subscription,
    billingHistory,
    loading,
    error,
    fetchSubscriptionDetail,
  } = useSubscriptionDetail();

  useEffect(() => {
    if (subscriptionId) {
      fetchSubscriptionDetail(subscriptionId);
    }
  }, [subscriptionId, fetchSubscriptionDetail]);

  const formatCurrency = (amount, currency = 'usd') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-900 text-green-300';
      case 'canceled':
        return 'bg-red-900 text-red-300';
      case 'past_due':
        return 'bg-yellow-900 text-yellow-300';
      default:
        return 'bg-gray-900 text-gray-300';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircleIcon className="h-4 w-4" />;
      case 'canceled':
        return <XCircleIcon className="h-4 w-4" />;
      case 'past_due':
        return <ClockIcon className="h-4 w-4" />;
      default:
        return <ClockIcon className="h-4 w-4" />;
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Loading subscription details...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-500">Error: {error}</div>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400">Subscription not found</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/Admin/Dashboard/Subscription')}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors"
          >
            <ArrowLeftIcon className="h-5 w-5" />
            <span>Back to Subscriptions</span>
          </button>
        </div>
        {/* <div className="text-sm text-gray-400">
          Subscription ID: {subscriptionId}
        </div> */}
      </div>

      {/* Subscription Detail Heading */}
      <h1 className="text-2xl font-bold text-white">Subscription Detail</h1>

      {/* Subscription Details Box */}
      <div className="bg-blueGray border border-borderGray rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* User Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">User Information</h3>
            <div className="space-y-6">
              <div className="flex items-center space-x-3">
                <UserIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Name</div>
                  <div className="text-white font-medium">{subscription.userId?.name || 'N/A'}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <EnvelopeIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Email</div>
                  <div className="text-white font-medium">{subscription.userId?.email || 'N/A'}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <CreditCardIcon className="h-5 w-5 text-gray-400" />
                <div>
                  <div className="text-sm text-gray-400">Stripe Customer ID</div>
                  <div className="text-white font-mono text-sm">{subscription.stripeCustomerId || 'N/A'}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Plan Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">Plan Information</h3>
            <div className="space-y-6">
              <div>
                <div className="text-sm text-gray-400">Plan Name</div>
                <div className="text-white font-medium text-lg">{subscription.planId?.name || 'N/A'}</div>
              </div>
              <div className="flex items-center space-x-3">
                {/* <CurrencyDollarIcon className="h-5 w-5 text-gray-400" /> */}
                <div>
                  <div className="text-sm text-gray-400">Price</div>
                  <div className="text-white font-medium">{formatCurrency(subscription.planId?.price || 0)}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Billing Interval</div>
                <div className="text-white font-medium capitalize">{subscription.planId?.interval || 'N/A'}</div>
              </div>
            </div>
          </div>

          {/* Subscription Status */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-white mb-4">Subscription Status</h3>
            <div className="space-y-6">
              <div>
                <div className="text-sm text-gray-400">Status</div>
                <div className="flex items-center space-x-2 mt-1">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${getStatusColor(subscription.status)}`}>
                    {getStatusIcon(subscription.status)}
                    <span className="ml-1 capitalize">{subscription.status || 'N/A'}</span>
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* <CalendarIcon className="h-5 w-5 text-gray-400" /> */}
                <div>
                  <div className="text-sm text-gray-400">Start Date</div>
                  <div className="text-white font-medium">{formatDate(subscription.startDate)}</div>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {/* <CalendarIcon className="h-5 w-5 text-gray-400" /> */}
                <div>
                  <div className="text-sm text-gray-400">Current Period End</div>
                  <div className="text-white font-medium">{formatDate(subscription.currentPeriodEnd)}</div>
                </div>
              </div>
              <div>
                <div className="text-sm text-gray-400">Cancel at Period End</div>
                <div className="text-white font-medium">{subscription.cancelAtPeriodEnd ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stripe Information */}
        {/* <div className="mt-6 pt-6 border-t border-borderGray">
          <h3 className="text-lg font-semibold text-white mb-4">Stripe Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-400">Subscription ID</div>
              <div className="text-white font-mono text-sm">{subscription.stripeSubscriptionId || 'N/A'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-400">Price ID</div>
              <div className="text-white font-mono text-sm">{subscription.stripePriceId || 'N/A'}</div>
            </div>
          </div>
        </div> */}
      </div>

      {/* Billing History Heading */}
      <h2 className="text-2xl font-bold text-white">Billing History</h2>

      {/* Billing History Table */}
      <div className="bg-blueGray border border-borderGray rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-blueGray border-b border-borderGray">
              <tr>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  Date
                </th>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider border-r border-borderGray">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-base font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-borderGray">
              {billingHistory && billingHistory.length > 0 ? (
                billingHistory.map((invoice) => (
                  <tr key={invoice.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                      <div className="flex items-center space-x-3">
                        <CalendarIcon className="h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm text-white font-medium">
                            {formatDate(invoice.created)}
                          </div>
                          <div className="text-xs text-gray-400">
                            Invoice #{invoice.number}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap border-r border-borderGray">
                      <div className="flex items-center space-x-2">
                        <CurrencyDollarIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-white font-medium">
                          {formatCurrency(invoice.amount, invoice.currency)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium ${
                        invoice.status === 'paid' 
                          ? 'bg-green-900 text-green-300' 
                          : invoice.status === 'open'
                          ? 'bg-yellow-900 text-yellow-300'
                          : 'bg-red-900 text-red-300'
                      }`}>
                        {invoice.status === 'paid' ? (
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <ClockIcon className="h-3 w-3 mr-1" />
                        )}
                        <span className="capitalize">{invoice.status}</span>
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="px-6 py-8 text-center text-gray-400">
                    No billing history found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
